/**
 * <AdminRetention /> — retention analytics dashboard fed by
 * subscription_cancellations + subscription_downgrades.
 *
 * KPIs shown:
 *   • Total cancellations vs. saves (with save-rate %)
 *   • Cancellation reasons (bar chart)
 *   • Would-return breakdown
 *   • Active subscribers by tier
 *   • Downgrade paths (retention wins)
 *   • Daily cancels timeline (last 30 days)
 *   • Recent feedback stream
 *
 * Zero external charting libs — CSS bar charts keep the bundle small.
 */
import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { TrendingDown, TrendingUp, Users, MessageSquare, Loader2, Filter } from "lucide-react";

const WINDOWS = [
  { d: 7, label: "7 days" },
  { d: 30, label: "30 days" },
  { d: 90, label: "90 days" },
  { d: 365, label: "12 months" },
];

export default function AdminRetention() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [analytics, cancels] = await Promise.all([
        http.get(`/admin/premium/retention?days=${days}`),
        http.get("/admin/premium/cancellations?limit=50"),
      ]);
      setData(analytics.data);
      setFeedback(cancels.data.cancellations || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  if (loading || !data) {
    return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  const wouldReturnTotal = data.would_return.yes + data.would_return.no + data.would_return.unknown;
  const yesPct = wouldReturnTotal ? Math.round((data.would_return.yes / wouldReturnTotal) * 100) : 0;

  return (
    <div data-testid="admin-retention" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline" style={{ color: "var(--wofy-primary)" }}>
            <TrendingDown size={12} className="inline -mt-0.5 mr-1" /> RETENTION
          </div>
          <h1 className="font-display text-3xl mt-1">Why people leave — and who we saved.</h1>
          <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>
            Fed by cancellation feedback and downgrade offers. Use this to tune pricing, plans, and messaging.
          </p>
        </div>
        <div className="inline-flex items-center gap-1 border rounded-full p-1" style={{ borderColor: "var(--wofy-line)" }} data-testid="retention-window">
          {WINDOWS.map((w) => (
            <button
              key={w.d}
              onClick={() => setDays(w.d)}
              className="text-xs uppercase font-semibold tracking-wider px-3 py-1 rounded-full transition"
              style={{
                background: days === w.d ? "var(--wofy-primary)" : "transparent",
                color: days === w.d ? "white" : "var(--wofy-text)",
              }}
              data-testid={`retention-window-${w.d}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile label="Active subscribers" value={data.totals.active_subscribers} icon={<Users className="w-4 h-4" />} accent="green" testid="kpi-active" />
        <KpiTile label={`Cancellations · ${days}d`} value={data.totals.cancellations} icon={<TrendingDown className="w-4 h-4" />} accent="red" testid="kpi-cancels" />
        <KpiTile label="Retention saves" value={data.totals.retention_saves} icon={<TrendingUp className="w-4 h-4" />} accent="green" testid="kpi-saves" />
        <KpiTile label="Save rate" value={`${data.totals.save_rate_pct}%`} icon={<TrendingUp className="w-4 h-4" />} accent="primary" testid="kpi-save-rate" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <BarSection title="Cancellation reasons" data={data.reasons} keyField="reason" testid="chart-reasons" empty="No cancellations in this window." />
        <div className="wofy-card" data-testid="chart-would-return">
          <h3 className="font-display text-lg mb-3">Would return?</h3>
          {wouldReturnTotal === 0 ? (
            <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>No responses yet.</p>
          ) : (
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="text-4xl font-display" style={{ color: "var(--wofy-primary)" }}>{yesPct}%</div>
                <div className="text-xs uppercase" style={{ color: "var(--wofy-text-soft)" }}>would return</div>
              </div>
              <div className="flex-1 space-y-1 text-sm">
                <StatRow label="Yes, someday" val={data.would_return.yes} total={wouldReturnTotal} colour="#16a34a" />
                <StatRow label="Probably not" val={data.would_return.no} total={wouldReturnTotal} colour="#dc2626" />
                <StatRow label="No answer" val={data.would_return.unknown} total={wouldReturnTotal} colour="#94a3b8" />
              </div>
            </div>
          )}
        </div>

        <BarSection title="Active subscribers by tier" data={data.active_by_tier} keyField="tier" testid="chart-active-by-tier" empty="No active subscribers yet." />
        <BarSection title="Cancels by tier" data={data.cancels_by_tier} keyField="tier" testid="chart-cancels-by-tier" empty="No cancellations in this window." />

        <BarSection title="Retention wins — downgrade paths" data={data.downgrade_paths} keyField="path" testid="chart-downgrades" empty="No downgrades in this window." />
        <div className="wofy-card" data-testid="chart-timeline">
          <h3 className="font-display text-lg mb-3">Daily cancels · last 30 days</h3>
          {data.timeline.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Quiet window — no cancels recorded.</p>
          ) : (
            <div className="flex items-end gap-1 h-32">
              {data.timeline.map((d) => {
                const max = Math.max(...data.timeline.map((x) => x.cancels), 1);
                const h = Math.round((d.cancels / max) * 100);
                return (
                  <div key={d.day} className="flex-1 flex flex-col items-center justify-end" title={`${d.day}: ${d.cancels}`}>
                    <div style={{ height: `${h}%`, background: "var(--wofy-primary)", opacity: 0.85, width: "100%", minHeight: 2, borderRadius: "3px 3px 0 0" }} />
                    <span className="text-[9px] opacity-50 mt-0.5">{d.day.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Feedback stream */}
      <div className="wofy-card">
        <h3 className="font-display text-lg mb-3">
          <MessageSquare className="inline -mt-1 w-5 h-5 mr-2" />
          Recent cancellation feedback
        </h3>
        {feedback.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Nothing yet — retention efforts are working, or nobody's cancelled.</p>
        ) : (
          <div className="space-y-3">
            {feedback.slice(0, 20).map((c) => (
              <div key={c.id} className="border-b pb-3 last:border-0 text-sm" style={{ borderColor: "var(--wofy-line)" }} data-testid={`feedback-row-${c.id}`}>
                <div className="flex items-center gap-2 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
                  <span className="font-mono">{new Date(c.cancelled_at).toLocaleDateString()}</span>
                  <span>·</span>
                  <span>{c.tier_name || "Unknown tier"}</span>
                  <span>·</span>
                  <span className="uppercase font-semibold">{c.reason || "no reason"}</span>
                  {c.would_return === true && <span className="ml-2 text-green-700">would return</span>}
                  {c.would_return === false && <span className="ml-2 text-red-600">won't return</span>}
                </div>
                {c.feedback && (
                  <p className="mt-1 whitespace-pre-line">{c.feedback}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function KpiTile({ label, value, icon, accent, testid }) {
  const bg = accent === "green" ? "#DCFCE7" : accent === "red" ? "#FEE2E2" : accent === "primary" ? "rgba(178,93,56,0.1)" : "#F3F4F6";
  const fg = accent === "green" ? "#16a34a" : accent === "red" ? "#dc2626" : accent === "primary" ? "var(--wofy-primary)" : "var(--wofy-text)";
  return (
    <div className="wofy-card" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase" style={{ color: "var(--wofy-text-soft)" }}>
        <span style={{ color: fg }}>{icon}</span>
        {label}
      </div>
      <div className="font-display text-3xl mt-2" style={{ color: fg }}>{value}</div>
    </div>
  );
}

function BarSection({ title, data, keyField, testid, empty }) {
  const rows = (data || []).slice(0, 10);
  const max = rows.length ? Math.max(...rows.map((r) => r.count), 1) : 1;
  return (
    <div className="wofy-card" data-testid={testid}>
      <h3 className="font-display text-lg mb-3">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>{empty}</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="text-sm">
              <div className="flex justify-between text-xs mb-1">
                <span className="opacity-80">{r[keyField]}</span>
                <span className="font-mono">{r.count}</span>
              </div>
              <div className="w-full rounded-full overflow-hidden h-2" style={{ background: "rgba(0,0,0,0.05)" }}>
                <div style={{ width: `${(r.count / max) * 100}%`, background: "var(--wofy-primary)", height: "100%" }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatRow({ label, val, total, colour }) {
  const pct = total ? Math.round((val / total) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-xs mb-0.5">
        <span>{label}</span>
        <span className="font-mono">{val} · {pct}%</span>
      </div>
      <div className="w-full rounded-full overflow-hidden h-1.5" style={{ background: "rgba(0,0,0,0.05)" }}>
        <div style={{ width: `${pct}%`, background: colour, height: "100%" }} />
      </div>
    </div>
  );
}
